// How the game runs on this phone (FE-28): frame times, ping round trips, reconnects and the page's
// web vitals, counted into the fixed buckets of @wahala/protocol and sent to the api once a minute
// (and when the page hides). Only counts and a device class leave the phone: no id, no name.
// Recording is a few array increments, so the frame loop can call it every frame.
import { bucketOf, type DeviceClass, TELEMETRY, type TelemetryReport } from '@wahala/protocol';

const SEND_EVERY_MS = 60_000;

/** Low: 4 cores or 4 GB or less (the renderer's low-end tier); high: 8 cores and 8 GB or more. */
export function deviceClass(nav: { hardwareConcurrency?: number; deviceMemory?: number } = navigator): DeviceClass {
  const cores = nav.hardwareConcurrency ?? 4;
  const memory = nav.deviceMemory ?? 4;
  if (cores <= 4 || memory <= 4) return 'low';
  if (cores >= 8 && memory >= 8) return 'high';
  return 'mid';
}

export class Telemetry {
  private frames = new Array<number>(TELEMETRY.frameMs.length + 1).fill(0);
  private rtts = new Array<number>(TELEMETRY.rttMs.length + 1).fill(0);
  private reconnects = { tried: 0, ok: 0, failed: 0 };
  private vitals: TelemetryReport['vitals'] = {};
  private lastFrame = 0;

  constructor(readonly device: DeviceClass) {}

  /** Call from the render loop with performance.now(); a gap after a pause is not a slow frame. */
  frame(now: number): void {
    const ms = now - this.lastFrame;
    const first = this.lastFrame === 0;
    this.lastFrame = now;
    if (first || ms <= 0 || ms > 1_000) return;
    const i = bucketOf(TELEMETRY.frameMs, ms);
    if ((this.frames[i] as number) < TELEMETRY.maxCount) this.frames[i] = (this.frames[i] as number) + 1;
  }

  rtt(ms: number): void {
    const i = bucketOf(TELEMETRY.rttMs, ms);
    if ((this.rtts[i] as number) < TELEMETRY.maxCount) this.rtts[i] = (this.rtts[i] as number) + 1;
  }

  reconnect(outcome: 'tried' | 'ok' | 'failed'): void {
    if (this.reconnects[outcome] < 1000) this.reconnects[outcome]++;
  }

  vital(name: 'lcp' | 'inp' | 'cls', value: number): void {
    this.vitals[name] = value;
  }

  /** The report so far, and a fresh start; null when there is nothing to say. */
  take(): TelemetryReport | null {
    const empty =
      this.frames.every((n) => n === 0) &&
      this.rtts.every((n) => n === 0) &&
      this.reconnects.tried + this.reconnects.ok + this.reconnects.failed === 0 &&
      Object.keys(this.vitals).length === 0;
    if (empty) return null;
    const r: TelemetryReport = {
      v: 1,
      device: this.device,
      frames: [...this.frames],
      rtt: [...this.rtts],
      reconnects: { ...this.reconnects },
      vitals: { ...this.vitals },
    };
    this.frames.fill(0);
    this.rtts.fill(0);
    this.reconnects = { tried: 0, ok: 0, failed: 0 };
    this.vitals = {};
    return r;
  }
}

/** The phone's one recorder; a no-op until started (and on phones outside the sample). */
let current: Telemetry | null = null;

export const telemetry = {
  frame: (now: number) => current?.frame(now),
  rtt: (ms: number) => current?.rtt(ms),
  reconnect: (outcome: 'tried' | 'ok' | 'failed') => current?.reconnect(outcome),
};

function send(): void {
  const r = current?.take();
  if (!r) return;
  // keepalive: the last report still goes when the page is closing
  fetch('/api/v1/telemetry', {
    method: 'POST',
    headers: { 'content-type': 'application/json' },
    body: JSON.stringify(r),
    keepalive: true,
  }).catch(() => {});
}

/** Start counting on this phone, if it falls in the sample (config.telemetry). */
export function startTelemetry(sampleRate: number): void {
  if (current || Math.random() >= sampleRate) return;
  current = new Telemetry(deviceClass());
  setInterval(send, SEND_EVERY_MS);
  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'hidden') send();
  });
  // the page's own vitals, from the small web-vitals library, loaded late
  import('web-vitals')
    .then(({ onLCP, onINP, onCLS }) => {
      onLCP((m) => current?.vital('lcp', m.value));
      onINP((m) => current?.vital('inp', m.value));
      onCLS((m) => current?.vital('cls', m.value));
    })
    .catch(() => {});
}
